<template>
  <tr
    class="k-table-row"
    :class="{ 'k-table-row-selected': selected, 'k-table-row-clickable': supportRowClick }"
    @click="$emit('row-click', row)"
    @keydown.enter="$emit('row-click', row)"
    @keydown.space="$emit('row-click', row)"
    tabindex="0"
    role="row"
    :aria-selected="selected"
  >
    <td v-for="key in columnOrder"
      :key="key"
      :class="getCellClasses(key, row[key])"
      role="cell"
    >
      <template v-if="headers[key].type === 'url' && row[key]">
        <router-link :to="row[key].path" :tag="row[key].tag">
          <div v-html="headers[key].displayAs(row[key])"></div>
        </router-link>
      </template>
      <template v-else-if="headers[key].type === 'external_url'">
        <a :href="row[key]" target="_blank">
          <span v-html="headers[key].displayAs(row[key])"></span>
          <i class="fa-solid fa-external-link-alt" aria-hidden="true"></i>
        </a>
      </template>
      <template v-else-if="headers[key].type === 'action'">
        <button
          class="btn-action-unstyled"
          @click.stop="!disabledAction(row[key]) && onActionClick(row[key], row.index, row, key)"
          :aria-label="`${key} ${row.name || 'this row'}`"
          v-html="headers[key].displayAs(row[key])"
        >
        </button>
      </template>
      <template v-else-if="headers[key].type === 'function'">
        <span v-html="headers[key].displayAs(row, row.index)"></span>
      </template>
      <template v-else-if="headers[key].type === 'string'">
        <span v-html="processedStrings[key]?.displayText || headers[key].displayAs(row[key])"></span>
        <button
          v-if="processedStrings[key]?.hasFullTextButton"
          class="btn btn-link"
          @click="viewFullText(key)"
          :aria-label="`View full text for ${headers[key].displayName || headers[key].name || key}`"
        >
          <i class="fa-solid fa-external-link-alt" aria-hidden="true"></i>
          See more
        </button>
      </template>
      <template v-else-if="headers[key].type === 'pill'">
        <div class="status-cell">
          <k-status-pill
            :type="pillConfig[key].type"
            :fill="pillConfig[key].fill"
            :text-color="pillConfig[key].textColor"
            :status-value="pillConfig[key].statusValue"
          >
            <template #label v-if="pillConfig[key].customHtml">
              <div v-html="pillConfig[key].customHtml"></div>
            </template>
          </k-status-pill>
        </div>
      </template>
      <template v-else>
        <span v-html="headers[key].displayAs(row[key])"></span>
      </template>
    </td>
  </tr>
</template>

<style scoped>
a {
  display: flex;
  align-items: center;
}

.fa-external-link-alt {
  margin-left: 0.25em;
  margin-top: 0.1em;
}

.k-table-action.clickable:hover button {
  color: var(--kate-type-link-hover);
  text-decoration: underline;
}

.k-table-row-clickable {
  cursor: pointer;
}

[data-theme="dark"] .k-table-row-clickable:hover {
  background-color: rgba(58 64 93 / 0.5) !important;
}

.k-table-row-clickable:hover {
  background-color: var(--kate-background-alt-alpha);
}
</style>

<script>
import KStatusPill from '@base-components/k-status-pill.vue';

export default {
  components: {
    KStatusPill,
  },
  props: {
    row: Object,
    headers: Object,
    columnOrder: Array,
    selected: Boolean,
    fixedColumnKeys: Array,
    onActionClick: Function,
    supportRowClick: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    processedStrings() {
      const stringConfig = {};
      this.columnOrder.forEach(key => {
        if (this.headers[key].type === 'string') {
          const fullText = this.headers[key].displayAs(this.row[key]);
          const displayLength = this.headers[key].options.displayLength || 160;
          const isTruncated = fullText?.length > displayLength;

          stringConfig[key] = {
            displayText: isTruncated ? `${fullText.substring(0, displayLength)}... ` : fullText,
            fullText,
            hasFullTextButton: isTruncated && !this.headers[key].options.hideFullTextButton,
          };
        }
      });
      return stringConfig;
    },

    pillConfig() {
      const pillHeaders = Object.keys(this.headers).filter(key => this.headers[key].type === 'pill');
      const config = {};

      pillHeaders.forEach(key => {
        config[key] = this.getPillConfig(key);
      });

      return config;
    },
  },
  methods: {
    disabledAction(action) {
      return action?.disabled;
    },

    getCellClasses(key, cell) {
      const classes = [
        'k-table-cell',
        `k-table-key-${key}`,
        `k-table-${this.headers[key].type}`,
      ];
      if (this.fixedColumnKeys.includes(key)) {
        classes.push('fixed-column');
      }
      if (this.isClickable(key, cell)) {
        classes.push('clickable');
      }
      const typeClasses = this.getHeaderTypeClasses(key, cell);
      if (typeClasses) {
        classes.push(typeClasses);
      }
      return classes.join(' ');
    },

    isClickable(key, cell) {
      return this.headers[key].type === 'action' && !this.disabledAction(cell);
    },

    getHeaderTypeClasses(key, cell) {
      const header = this.headers[key];
      switch (header.type) {
        case 'rag_status':
          return this.getRagStatusRowClasses(header, cell);
        case 'percentage':
        case 'number':
          return this.getNumRowClasses(header, cell);
        case 'boolean':
          return 'k-table-cell-container';
        default:
          return '';
      }
    },

    getNumRowClasses(header, cell) {
      if (!header.options.color) return '';
      if (cell >= 70) return 'k-success';
      if (cell > 30) return 'k-warning';
      if (cell >= 0 && cell != null) return 'k-poor-score';
      return '';
    },

    getRagStatusRowClasses(header, cell) {
      if (!header.options.color) return '';
      if (cell === 'Green') return 'k-success';
      if (cell === 'Amber') return 'k-warning';
      if (cell === 'Red' && cell != null) return 'k-poor-score';
      return '';
    },

    viewFullText(key) {
      this.$emit('viewFullText', key, this.row[key]);
    },

    getPillConfig(key) {
      const statusValue = this.headers[key].displayAs(this.row[key]);
      const headerOptions = this.headers[key].options || {};

      // pill config options may be passed as functions or strings
      const getConfigValue = option => {
        if (!option) return '';
        return typeof option === 'function' ? option(statusValue) : option;
      };

      return {
        statusValue,
        type: getConfigValue(headerOptions?.type),
        fill: getConfigValue(headerOptions?.fill),
        textColor: getConfigValue(headerOptions?.textColor),
        customHtml: getConfigValue(headerOptions?.customHtml),
      };
    },

  },
};
</script>
